import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Cookie utilities
const COOKIE_CONSENT_KEY = 'fluturo_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'fluturo_cookie_preferences';

const cookieUtils = {
  // Set cookie consent status
  setConsent: (preferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    
    // Set actual cookies based on preferences
    if (preferences.essential) {
      // Essential cookies are always set
      document.cookie = "essential=true; path=/; max-age=31536000"; // 1 year
    }
    
    if (preferences.analytics) {
      document.cookie = "analytics=true; path=/; max-age=31536000";
      // Initialize analytics here (Google Analytics, etc.)
    }
    
    if (preferences.functional) {
      document.cookie = "functional=true; path=/; max-age=31536000";
      // Enable functional features
    }
    
    if (preferences.marketing) {
      document.cookie = "marketing=true; path=/; max-age=31536000";
      // Initialize marketing pixels, retargeting, etc.
    }
  },
  
  // Get cookie consent status
  getConsent: () => {
    return localStorage.getItem(COOKIE_CONSENT_KEY) === 'true';
  },
  
  // Get cookie preferences
  getPreferences: () => {
    const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    return prefs ? JSON.parse(prefs) : {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false
    };
  },
  
  // Clear all cookies
  clearCookies: () => {
    document.cookie = "essential=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    document.cookie = "analytics=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    document.cookie = "functional=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    document.cookie = "marketing=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
  }
};

// Styled Components
const BannerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-top: 1px solid #e0e0e0;
  padding: 20px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  transform: translateY(${props => props.isVisible ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const BannerText = styled.div`
  flex: 1;
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  
  a {
    color: #003663;
    text-decoration: underline;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 20px;
  }
  
  &.primary {
    background: #003663;
    color: white;
    
    &:hover {
      background: #002547;
    }
  }
  
  &.secondary {
    background: white;
    color: #003663;
    border: 2px solid #003663;
    
    &:hover {
      background: #f8f9fa;
    }
  }
  
  &.tertiary {
    background: transparent;
    color: #666;
    border: 1px solid #ddd;
    
    &:hover {
      background: #f8f9fa;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  transform: translateY(${props => props.isVisible ? '0' : '20px'});
  transition: transform 0.3s ease;
  
  @media (max-width: 768px) {
    width: 95%;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 20px;
  
  h2 {
    margin: 0 0 16px 0;
    color: #003663;
    font-size: 24px;
    font-weight: 700;
  }
  
  p {
    margin: 0 0 20px 0;
    color: #666;
    line-height: 1.6;
  }
`;

const ModalBody = styled.div`
  padding: 0 24px 24px;
`;

const CookieCategory = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  
  &.always-active {
    background: #f8f9fa;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  
  h3 {
    margin: 0;
    color: #003663;
    font-size: 16px;
    font-weight: 600;
  }
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 24px;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
  
  input:checked + .slider {
    background-color: #003663;
  }
  
  input:checked + .slider:before {
    transform: translateX(26px);
  }
  
  &.disabled .slider {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CategoryDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const CookieConsent = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    functional: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = cookieUtils.getConsent();
    
    if (!hasConsent) {
      // Show banner after 3-4 seconds
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 3500); // 3.5 seconds
      
      return () => clearTimeout(timer);
    } else {
      // Load existing preferences
      setPreferences(cookieUtils.getPreferences());
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true
    };
    
    cookieUtils.setConsent(allAccepted);
    setPreferences(allAccepted);
    setShowBanner(false);
    setShowModal(false);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false
    };
    
    cookieUtils.setConsent(essentialOnly);
    setPreferences(essentialOnly);
    setShowBanner(false);
    setShowModal(false);
  };

  const handleSavePreferences = () => {
    cookieUtils.setConsent(preferences);
    setShowBanner(false);
    setShowModal(false);
  };

  const handleManagePreferences = () => {
    setShowModal(true);
  };

  const handleToggle = (category) => {
    if (category === 'essential') return; // Can't toggle essential cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const categories = [
    {
      key: 'essential',
      title: t('Essential'),
      description: t('Session management and user authentication'),
      alwaysActive: true
    },
    {
      key: 'analytics',
      title: t('Analytics'),
      description: t('User journey analysis and conversion tracking'),
      alwaysActive: false
    },
    {
      key: 'functional',
      title: t('Functional'),
      description: t('Language and region preferences'),
      alwaysActive: false
    },
    {
      key: 'marketing',
      title: t('Marketing'),
      description: t('Personalized flight deals and offers'),
      alwaysActive: false
    }
  ];

  if (!showBanner && !showModal) return null;

  return (
    <>
      {/* Cookie Banner */}
      <BannerContainer isVisible={showBanner}>
        <BannerContent>
          <BannerText>
            {t('cookie_consent_message')}
            <a href="/cookie-policy" target="_blank">{t('Cookie Policy')}</a>.
          </BannerText>
          <ButtonGroup>
            <Button className="primary" onClick={handleAcceptAll}>
              {t('accept_all_cookies')}
            </Button>
            <Button className="secondary" onClick={handleManagePreferences}>
              {t('Manage Cookie Preferences')}
            </Button>
            <Button className="tertiary" onClick={handleRejectNonEssential}>
              {t('reject_non_essential')}
            </Button>
          </ButtonGroup>
        </BannerContent>
      </BannerContainer>

      {/* Detailed Modal */}
      <ModalOverlay isVisible={showModal} onClick={() => setShowModal(false)}>
        <ModalContent isVisible={showModal} onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <h2>{t('Manage Cookie Preferences')}</h2>
            <p>
              {t('cookie_preferences_description')}
            </p>
          </ModalHeader>
          
          <ModalBody>
            {categories.map((category) => (
              <CookieCategory key={category.key} className={category.alwaysActive ? 'always-active' : ''}>
                <CategoryHeader>
                  <h3>
                    {category.title}
                    {category.alwaysActive && <span style={{color: '#666', fontWeight: 'normal'}}> ({t('always_active')})</span>}
                  </h3>
                  <Toggle className={category.alwaysActive ? 'disabled' : ''}>
                    <input
                      type="checkbox"
                      checked={preferences[category.key]}
                      onChange={() => handleToggle(category.key)}
                      disabled={category.alwaysActive}
                    />
                    <span className="slider"></span>
                  </Toggle>
                </CategoryHeader>
                <CategoryDescription>
                  {category.description}
                </CategoryDescription>
              </CookieCategory>
            ))}
            
            <p style={{color: '#666', fontSize: '14px', marginTop: '20px'}}>
              {t('You can change your preferences at any time by visiting our')} <a href="/cookie-policy" target="_blank">{t('Cookie Policy')}</a>.
            </p>
          </ModalBody>
          
          <ModalFooter>
            <Button className="tertiary" onClick={handleRejectNonEssential}>
              {t('reject_all')}
            </Button>
            <Button className="secondary" onClick={handleSavePreferences}>
              {t('save_preferences')}
            </Button>
            <Button className="primary" onClick={handleAcceptAll}>
              {t('accept_all_cookies')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </>
  );
}; 